import PortalStudentProfile from '@classes/StudentProfile'
import BackButton from '@components/BackButton'
import PageHeader from '@components/PageHeader'
import PageTitle from '@components/PageTitle'
import usePortalFeatures from '@components/RoleBasedView/usePortalFeatures'
import WithPortalAuthorization from '@hocs/WithPortalAuthorization'
import { StoreProps } from '@interfaces/StoreState'
import ProgramSelector from '@layouts/Dashboard/HeroBlock/ProgramSelector'
import Layout from '@layouts/LearningPath/Layout'
import { LPContextProvider } from '@layouts/LearningPath/Layout/Context/LPContext'
import Loader from '@layouts/LearningPath/Layout/Loader'
import ParentLayout from '@layouts/LearningPath/Layout/ParentLayout'
import { PageHeaderCtr } from '@layouts/LearningPath/Layout/style'
import QueryLearningPath from '@layouts/LearningPath/QueryLearningPathv2'
import { PROFILE_TYPE } from '@utils/constants'
import { getStoredMaterials, getStoredPrograms, getStoredRegistrations } from '@utils/helpers/material'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { getStorageAdapter, lpHashKey } from '@shell/adapters/storage'

const LearningPath: React.FC = () => {
  const back = '/'
  const { studentProfile, activeMaterial, activeProgram, activeRegistration, profile } = useSelector(
    ({ userProfile, dashboard }: StoreProps) => ({
      studentProfile: new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo),
      activeMaterial: dashboard.activeMaterial || undefined,
      activeProgram: dashboard.activeProgram || undefined,
      activeRegistration: dashboard.activeRegistration || undefined,
      profile: userProfile.info,
    })
  )
  const { state } = usePortalFeatures({
    configId: 'flag::industrySpecificContent',
  })
  useEffect(() => {
    const storedHash = getStorageAdapter().get(lpHashKey(studentProfile.SFId)) || ''
    const storedLocationHash = storedHash ? (storedHash.startsWith('#') ? storedHash : `#${storedHash}`) : ''

    if (window.location.hash !== storedLocationHash) {
      window.location.hash = storedHash
    }
  }, [studentProfile.SFId])
  const ActiveIdFromMaterialDropdown = studentProfile?.getActiveIdFromMaterialDropdown()
  const activeDropdownLPId = ActiveIdFromMaterialDropdown?.materialLPID
  const activeDropdownRegistrationId = ActiveIdFromMaterialDropdown?.registrationId
  const activeDropdownProgramId = ActiveIdFromMaterialDropdown?.programId
  const userSelectedMaterial = getStoredMaterials()?.[studentProfile.SFId]
  const userSelectedRegistrationId = getStoredRegistrations()?.[studentProfile.SFId]
  const userSelectedProgramId = getStoredPrograms()?.[studentProfile.SFId]
  const selected = activeMaterial || userSelectedMaterial || activeDropdownLPId
  const selectedProgramId = activeProgram || userSelectedProgramId || activeDropdownProgramId
  const selectedRegistrationId = activeRegistration || userSelectedRegistrationId || activeDropdownRegistrationId
  const { allowed } = state
  return (
    <>
      <PageTitle title="My learning path" />
      <ProgramSelector showCT hide defaultProgram={selected} />
      <PageHeaderCtr>
        <PageHeader title="My learning path" noClock noHR={true} />
      </PageHeaderCtr>
      {profile?.Type === PROFILE_TYPE.INSTRUCTOR && <BackButton back={back} />}
      {allowed ? (
        <ParentLayout />
      ) : (
        <QueryLearningPath level="all">
          {({ loading, error, LearningPaths }) => {
            return loading ? (
              <Loader />
            ) : (
              <LPContextProvider>
                <Layout
                  key={LearningPaths?.learningPaths?.length}
                  error={error}
                  LearningPaths={LearningPaths}
                  activeDropdownLPId={selected}
                  activeDropdownRegistrationId={selectedRegistrationId}
                  activeDropdownProgramId={selectedProgramId}
                />
              </LPContextProvider>
            )
          }}
        </QueryLearningPath>
      )}
    </>
  )
}

export default WithPortalAuthorization(LearningPath, {
  id: 'page::learning-path',
})
